import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Bell, FileText, Plus, Trash2, Edit, Upload, X, ExternalLink, Award } from "lucide-react";

const CATEGORIES = [
  { value: "notice_board", label: "Notice Board" },
  { value: "whats_new", label: "What's New" },
  { value: "important_notices", label: "Important Notices" },
  { value: "latest_update", label: "Latest Update" },
];
const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

function FileUploadField({ value, filename, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(res.data.file_id, res.data.original_filename);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="upload-field">
      <label className="upload-btn" data-testid="upload-file-btn">
        <Upload size={14} /> {busy ? "Uploading..." : "Choose PDF"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handle} hidden disabled={busy} data-testid="file-input" />
      </label>
      {value && <span className="upload-ok">✓ {filename || "file attached"}</span>}
      {err && <span className="upload-err">{err}</span>}
    </div>
  );
}

const emptyNotice = { category: "whats_new", title: "", link_type: "none", external_link: "", file_id: "", is_new: false, date: "" };
const emptyResult = { title: "", advt_no: "", post: "", link_type: "file", external_link: "", file_id: "", date: "" };

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("notices");
  const [notices, setNotices] = useState([]);
  const [results, setResults] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [modal, setModal] = useState(null); // { type, data, editing }
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/notices").then((r) => setNotices(r.data));
    api.get("/admin/results").then((r) => setResults(r.data));
    api.get("/admin/score-cards").then((r) => setScorecards(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const openNew = (type) => {
    setError("");
    setFileName("");
    setModal({ type, editing: false, data: type === "notice" ? { ...emptyNotice } : { ...emptyResult } });
  };
  const openEdit = (type, row) => {
    setError("");
    setFileName("");
    setModal({ type, editing: true, data: { ...row } });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    const { type, editing, data } = modal;
    const base = type === "notice" ? "/admin/notices" : type === "scorecard" ? "/admin/score-cards" : "/admin/results";
    const payload = { ...data };
    delete payload.id; delete payload.is_deleted; delete payload.created_at;
    try {
      if (editing) await api.put(`${base}/${data.id}`, payload);
      else await api.post(base, payload);
      setModal(null);
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    const base = type === "notice" ? "/admin/notices" : type === "scorecard" ? "/admin/score-cards" : "/admin/results";
    await api.delete(`${base}/${id}`);
    load();
  };

  const upd = (patch) => setModal((m) => ({ ...m, data: { ...m.data, ...patch } }));

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <div className="admin-brand">
          <span className="admin-logo">BTSC</span>
          <div>
            <strong>Admin Panel</strong>
            <small>Bihar Technical Service Commission</small>
          </div>
        </div>
        <div className="admin-user">
          <a href="/" target="_blank" rel="noreferrer" className="view-site">View Site <ExternalLink size={13} /></a>
          <span>{admin?.email}</span>
          <button onClick={doLogout} data-testid="admin-logout"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <div className="admin-tabs">
        <button className={tab === "notices" ? "active" : ""} onClick={() => setTab("notices")} data-testid="tab-notices">
          <Bell size={15} /> Notifications & Notices <span className="count">{notices.length}</span>
        </button>
        <button className={tab === "results" ? "active" : ""} onClick={() => setTab("results")} data-testid="tab-results">
          <FileText size={15} /> Results <span className="count">{results.length}</span>
        </button>
        <button className={tab === "scorecards" ? "active" : ""} onClick={() => setTab("scorecards")} data-testid="tab-scorecards">
          <Award size={15} /> Score Cards <span className="count">{scorecards.length}</span>
        </button>
      </div>

      <div className="admin-body">
        {tab === "notices" && (
          <section>
            <div className="section-head">
              <h2>Notifications & Notices</h2>
              <button className="add-btn" onClick={() => openNew("notice")} data-testid="add-notice-btn"><Plus size={16} /> Add Notice</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>Category</th><th>Title</th><th>Type</th><th>New</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {notices.length === 0 && <tr><td colSpan={6} className="td-empty">No notices yet.</td></tr>}
                {notices.map((n) => (
                  <tr key={n.id} data-testid={`notice-row-${n.id}`}>
                    <td><span className="chip">{catLabel(n.category)}</span></td>
                    <td className="td-title">{n.title}</td>
                    <td>{n.link_type}</td>
                    <td>{n.is_new ? "Yes" : "-"}</td>
                    <td>{n.date}</td>
                    <td className="row-actions">
                      <button onClick={() => openEdit("notice", n)} data-testid={`edit-notice-${n.id}`}><Edit size={14} /></button>
                      <button className="danger" onClick={() => remove("notice", n.id)} data-testid={`delete-notice-${n.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === "results" && (
          <section>
            <div className="section-head">
              <h2>Results</h2>
              <button className="add-btn" onClick={() => openNew("result")} data-testid="add-result-btn"><Plus size={16} /> Upload Result</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>Title</th><th>Advt. No.</th><th>Post</th><th>Type</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {results.length === 0 && <tr><td colSpan={6} className="td-empty">No results yet.</td></tr>}
                {results.map((r) => (
                  <tr key={r.id} data-testid={`result-admin-row-${r.id}`}>
                    <td className="td-title">{r.title}</td>
                    <td>{r.advt_no}</td>
                    <td>{r.post}</td>
                    <td>{r.link_type === "file" ? "PDF" : "Link"}</td>
                    <td>{r.date}</td>
                    <td className="row-actions">
                      <button onClick={() => openEdit("result", r)} data-testid={`edit-result-${r.id}`}><Edit size={14} /></button>
                      <button className="danger" onClick={() => remove("result", r.id)} data-testid={`delete-result-${r.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {tab === "scorecards" && (
          <section>
            <div className="section-head">
              <h2>Score Cards</h2>
              <button className="add-btn" onClick={() => openNew("scorecard")} data-testid="add-scorecard-btn"><Plus size={16} /> Upload Score Card</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>Title</th><th>Advt. No.</th><th>Post</th><th>Type</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {scorecards.length === 0 && <tr><td colSpan={6} className="td-empty">No score cards yet.</td></tr>}
                {scorecards.map((r) => (
                  <tr key={r.id} data-testid={`scorecard-admin-row-${r.id}`}>
                    <td className="td-title">{r.title}</td>
                    <td>{r.advt_no}</td>
                    <td>{r.post}</td>
                    <td>{r.link_type === "file" ? "PDF" : "Link"}</td>
                    <td>{r.date}</td>
                    <td className="row-actions">
                      <button onClick={() => openEdit("scorecard", r)} data-testid={`edit-scorecard-${r.id}`}><Edit size={14} /></button>
                      <button className="danger" onClick={() => remove("scorecard", r.id)} data-testid={`delete-scorecard-${r.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
            <div className="modal-head">
              <h3>{modal.editing ? "Edit" : "Add"} {modal.type === "notice" ? "Notice" : modal.type === "scorecard" ? "Score Card" : "Result"}</h3>
              <button onClick={() => setModal(null)} data-testid="modal-close"><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="login-error">{error}</div>}

              {modal.type === "notice" && (
                <>
                  <label>Category</label>
                  <select value={modal.data.category} onChange={(e) => upd({ category: e.target.value })} data-testid="notice-category">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </>
              )}

              <label>Title *</label>
              <input value={modal.data.title} onChange={(e) => upd({ title: e.target.value })} data-testid="field-title" />

              {modal.type !== "notice" && (
                <div className="two-col">
                  <div>
                    <label>Advt. No.</label>
                    <input value={modal.data.advt_no} onChange={(e) => upd({ advt_no: e.target.value })} data-testid="field-advt" />
                  </div>
                  <div>
                    <label>Post</label>
                    <input value={modal.data.post} onChange={(e) => upd({ post: e.target.value })} data-testid="field-post" />
                  </div>
                </div>
              )}

              <label>Attachment Type</label>
              <select value={modal.data.link_type} onChange={(e) => upd({ link_type: e.target.value })} data-testid="field-link-type">
                {modal.type === "notice" && <option value="none">No link</option>}
                <option value="file">Upload PDF file</option>
                <option value="external">External link</option>
              </select>

              {modal.data.link_type === "file" && (
                <>
                  <label>Upload PDF</label>
                  <FileUploadField
                    value={modal.data.file_id}
                    filename={fileName}
                    onUploaded={(id, name) => { upd({ file_id: id }); setFileName(name); }}
                  />
                </>
              )}

              {modal.data.link_type === "external" && (
                <>
                  <label>External Link (URL)</label>
                  <input value={modal.data.external_link} onChange={(e) => upd({ external_link: e.target.value })} placeholder="https://..." data-testid="field-external-link" />
                </>
              )}

              <div className="two-col">
                <div>
                  <label>Date (optional)</label>
                  <input value={modal.data.date} onChange={(e) => upd({ date: e.target.value })} placeholder="dd-mm-yyyy" data-testid="field-date" />
                </div>
                {modal.type === "notice" && (
                  <div className="checkbox-row">
                    <label className="chk">
                      <input type="checkbox" checked={!!modal.data.is_new} onChange={(e) => upd({ is_new: e.target.checked })} data-testid="field-is-new" />
                      Mark as "New"
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-cancel" onClick={() => setModal(null)} data-testid="modal-cancel">Cancel</button>
              <button className="btn-save" onClick={save} disabled={saving || !modal.data.title} data-testid="modal-save">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
